<script setup>
/**
 * School Autocomplete Component with Add/Edit capability
 * Provides live search for schools as user types
 * Allows adding new schools if not found
 */
import { ref, watch, computed } from 'vue';

const props = defineProps({
  modelValue: { type: String, default: '' },
  placeholder: { type: String, default: 'Okul adını girin' }
});

const emit = defineEmits(['update:modelValue', 'school-selected']);

const inputValue = ref(props.modelValue || '');
const schools = ref([]);
const showDropdown = ref(false);
const loading = ref(false);
const searchTimeout = ref(null);
const showAddModal = ref(false);
const newSchool = ref({ kurum: '', il: '', ilce: '' });
const saving = ref(false);

const API_BASE = '/api/v1/schools';

// Sync with parent
watch(() => props.modelValue, (newVal) => {
  if (newVal !== inputValue.value) {
    inputValue.value = newVal || '';
  }
});

// Search when user types
watch(inputValue, (newVal) => {
  if (searchTimeout.value) clearTimeout(searchTimeout.value);

  // Emit value to parent
  emit('update:modelValue', newVal);

  if (newVal.length >= 2) {
    searchTimeout.value = setTimeout(() => searchSchools(), 300);
  } else {
    schools.value = [];
    showDropdown.value = false;
  }
});

const searchSchools = async () => {
  if (inputValue.value.length < 2) return;

  loading.value = true;
  try {
    const response = await fetch(`${API_BASE}/search?q=${encodeURIComponent(inputValue.value)}`);
    schools.value = await response.json();
    showDropdown.value = true;
  } catch (e) {
    console.error('Failed to search schools:', e);
    schools.value = [];
  }
  loading.value = false;
};

const selectSchool = (school) => {
  inputValue.value = `${school.kurum} (${school.ilce}/${school.il})`;
  emit('update:modelValue', inputValue.value);
  emit('school-selected', school);
  showDropdown.value = false;
  schools.value = [];
};

const handleBlur = () => {
  setTimeout(() => {
    showDropdown.value = false;
  }, 200);
};

const handleFocus = () => {
  if (schools.value.length > 0) {
    showDropdown.value = true;
  }
};

const openAddModal = () => {
  newSchool.value = { kurum: inputValue.value, il: '', ilce: '' };
  showDropdown.value = false;
  showAddModal.value = true;
};

const closeAddModal = () => {
  showAddModal.value = false;
  newSchool.value = { kurum: '', il: '', ilce: '' };
};

const saveNewSchool = async () => {
  if (!newSchool.value.kurum || !newSchool.value.il || !newSchool.value.ilce) {
    alert('Lütfen tüm alanları doldurun');
    return;
  }

  saving.value = true;
  try {
    const response = await fetch(API_BASE, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        school: {
          kurum: newSchool.value.kurum,
          il: newSchool.value.il.toUpperCase(),
          ilce: newSchool.value.ilce.toUpperCase(),
          ilkodu: 0,
          ilcekodu: 0
        }
      })
    });

    if (response.ok) {
      const school = await response.json();
      inputValue.value = `${school.kurum} (${school.ilce}/${school.il})`;
      emit('update:modelValue', inputValue.value);
      emit('school-selected', school);
      closeAddModal();
    } else {
      const error = await response.json();
      alert('Hata: ' + (error.errors?.join(', ') || 'Okul eklenemedi'));
    }
  } catch (e) {
    console.error('Failed to save school:', e);
    alert('Okul eklenirken hata oluştu');
  }
  saving.value = false;
};

// Inline styles
const containerStyle = {
  position: 'relative',
  width: '100%'
};

const inputStyle = {
  width: '100%',
  padding: '8px 12px',
  border: '1px solid #d1d5db',
  borderRadius: '6px',
  fontSize: '14px',
  outline: 'none',
  backgroundColor: '#ffffff',
  color: '#111827',
  boxSizing: 'border-box'
};

const dropdownStyle = {
  position: 'absolute',
  top: '100%',
  left: '0',
  right: '0',
  backgroundColor: '#ffffff',
  border: '1px solid #e5e7eb',
  borderRadius: '8px',
  marginTop: '4px',
  maxHeight: '280px',
  overflowY: 'auto',
  zIndex: '9999',
  boxShadow: '0 10px 25px rgba(0,0,0,0.15)'
};

const schoolItemStyle = {
  padding: '10px 12px',
  cursor: 'pointer',
  borderBottom: '1px solid #f3f4f6',
  transition: 'background-color 0.15s'
};

const schoolNameStyle = {
  fontWeight: '500',
  fontSize: '13px',
  color: '#111827'
};

const schoolLocationStyle = {
  fontSize: '11px',
  color: '#6b7280',
  marginTop: '2px'
};

const loadingStyle = {
  padding: '12px',
  textAlign: 'center',
  color: '#6b7280',
  fontSize: '13px'
};

const addButtonStyle = {
  display: 'block',
  width: '100%',
  padding: '12px',
  backgroundColor: '#f0fdf4',
  border: 'none',
  borderTop: '1px solid #e5e7eb',
  cursor: 'pointer',
  fontSize: '13px',
  color: '#166534',
  fontWeight: '500',
  textAlign: 'center'
};

const modalOverlayStyle = {
  position: 'fixed',
  top: '0',
  left: '0',
  right: '0',
  bottom: '0',
  backgroundColor: 'rgba(0, 0, 0, 0.6)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: '10000'
};

const modalStyle = {
  backgroundColor: '#ffffff',
  borderRadius: '12px',
  width: '90%',
  maxWidth: '450px',
  padding: '24px',
  boxShadow: '0 25px 50px rgba(0,0,0,0.25)'
};

const modalTitleStyle = {
  fontSize: '18px',
  fontWeight: '600',
  color: '#111827',
  marginBottom: '20px'
};

const formGroupStyle = {
  marginBottom: '16px'
};

const labelStyle = {
  display: 'block',
  fontSize: '13px',
  fontWeight: '500',
  color: '#374151',
  marginBottom: '6px'
};

const modalInputStyle = {
  width: '100%',
  padding: '10px 12px',
  border: '1px solid #d1d5db',
  borderRadius: '6px',
  fontSize: '14px',
  outline: 'none',
  boxSizing: 'border-box'
};

const modalButtonsStyle = {
  display: 'flex',
  gap: '12px',
  justifyContent: 'flex-end',
  marginTop: '24px'
};

const cancelBtnStyle = {
  padding: '10px 20px',
  backgroundColor: '#e5e7eb',
  border: 'none',
  borderRadius: '6px',
  cursor: 'pointer',
  fontSize: '14px',
  color: '#374151',
  fontWeight: '500'
};

const saveBtnStyle = {
  padding: '10px 20px',
  backgroundColor: '#2563eb',
  border: 'none',
  borderRadius: '6px',
  cursor: 'pointer',
  fontSize: '14px',
  color: '#ffffff',
  fontWeight: '500'
};
</script>

<template>
  <div :style="containerStyle">
    <input
      v-model="inputValue"
      type="text"
      :style="inputStyle"
      :placeholder="placeholder"
      @focus="handleFocus"
      @blur="handleBlur"
    />

    <!-- Loading indicator -->
    <div v-if="loading" :style="dropdownStyle">
      <div :style="loadingStyle">🔍 Aranıyor...</div>
    </div>

    <!-- Dropdown results -->
    <div v-else-if="showDropdown" :style="dropdownStyle">
      <div
        v-for="school in schools.slice(0, 8)"
        :key="school.id"
        :style="schoolItemStyle"
        @mousedown.prevent="selectSchool(school)"
        @mouseenter="$event.target.style.backgroundColor = '#eff6ff'"
        @mouseleave="$event.target.style.backgroundColor = '#ffffff'"
      >
        <div :style="schoolNameStyle">{{ school.kurum }}</div>
        <div :style="schoolLocationStyle">📍 {{ school.ilce }} / {{ school.il }}</div>
      </div>

      <!-- Add new school button -->
      <button
        :style="addButtonStyle"
        @mousedown.prevent="openAddModal"
        @mouseenter="$event.target.style.backgroundColor = '#dcfce7'"
        @mouseleave="$event.target.style.backgroundColor = '#f0fdf4'"
      >
        ➕ Okul bulunamadı mı? Yeni okul ekle
      </button>
    </div>

    <!-- Add School Modal -->
    <div v-if="showAddModal" :style="modalOverlayStyle" @click.self="closeAddModal">
      <div :style="modalStyle">
        <h3 :style="modalTitleStyle">🏫 Yeni Okul Ekle</h3>

        <div :style="formGroupStyle">
          <label :style="labelStyle">Okul Adı *</label>
          <input
            v-model="newSchool.kurum"
            type="text"
            :style="modalInputStyle"
            placeholder="Okul adını girin"
          />
        </div>

        <div :style="formGroupStyle">
          <label :style="labelStyle">İl *</label>
          <input
            v-model="newSchool.il"
            type="text"
            :style="modalInputStyle"
            placeholder="Örn: ANKARA"
          />
        </div>

        <div :style="formGroupStyle">
          <label :style="labelStyle">İlçe *</label>
          <input
            v-model="newSchool.ilce"
            type="text"
            :style="modalInputStyle"
            placeholder="Örn: ÇANKAYA"
          />
        </div>

        <div :style="modalButtonsStyle">
          <button :style="cancelBtnStyle" @click="closeAddModal">İptal</button>
          <button
            :style="saveBtnStyle"
            @click="saveNewSchool"
            :disabled="saving"
          >
            {{ saving ? 'Kaydediliyor...' : 'Kaydet' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
